import React, { useState, useRef, useEffect } from 'react';
import { useBranding } from '@/contexts/BrandingContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

interface LogoUploadProps {
  value: string | undefined;
  type: 'logo' | 'favicon';
  title: string;
  description: string;
  maxSize?: number; // in MB
  acceptedFormats?: string[];
  onChange?: (url: string) => void;
}

export function LogoUpload({ 
  value,
  type, 
  title, 
  description, 
  maxSize = 5,
  acceptedFormats = ['PNG', 'JPEG', 'JPG', 'SVG', 'WebP'],
  onChange
}: LogoUploadProps) {
  const { branding, uploadLogo, deleteLogo, isWhiteLabel, isLoading } = useBranding();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Priority: Preview -> Local -> Server -> Parent value
  const serverUrl = type === 'logo' ? branding.logoUrl : branding.faviconUrl;
  const displayUrl = previewUrl || localImageUrl || serverUrl || value || '';
  // Update local state when value changes from parent (manual input)
  useEffect(() => {
    if (value && value !== localImageUrl && value !== serverUrl) {
      setLocalImageUrl(value);
    }
  }, [value, localImageUrl, serverUrl]);

  // Update local state when server data changes after successful operations
  useEffect(() => {
    if (serverUrl && !localImageUrl && !previewUrl) {
      setLocalImageUrl(serverUrl);
    }
  }, [serverUrl, localImageUrl, previewUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = async (file: File) => {
    if (!isWhiteLabel) {
      toast.error('Only white-label partners can upload logos');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(`Invalid file type. Allowed formats: ${acceptedFormats.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${maxSize}MB`);
      return;
    }
    const fileUrl = URL.createObjectURL(file);
    setPreviewUrl(fileUrl);

    setUploading(true);
    try {
      const uploadedUrl = await uploadLogo(file, type);
      // Update local state with new URL from server
      setLocalImageUrl(uploadedUrl);
      // Notify parent about the change
      onChange?.(uploadedUrl);
      URL.revokeObjectURL(fileUrl);
      setPreviewUrl(null);
      toast.success(`${title} uploaded successfully`);
    } catch (error) {
      console.error('Upload failed:', error);
      URL.revokeObjectURL(fileUrl);
      setPreviewUrl(null);
      toast.error(`Failed to upload ${title}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input value to allow uploading the same file again
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDelete = async () => {
    if (!isWhiteLabel) {
      toast.error('Only white-label partners can delete logos');
      return;
    }

    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      try {
        await deleteLogo(type);
        // Clear all states
        setLocalImageUrl('');
        onChange?.('');
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
        toast.success(`${title} deleted successfully`);
      } catch (error) {
        console.error('Delete failed:', error);
        toast.error(`Failed to delete ${title}`);
      }
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  if (!isWhiteLabel) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className="w-full max-w-md flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            {title}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{description}</p>
        </CardHeader>
        <CardContent className="space-y-4 flex flex-1 flex-col justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Loading branding...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          {title}
        </CardTitle>
        <p className="text-sm text-muted-foreground ">{description}</p>
      </CardHeader>
      <CardContent className="space-y-4 flex flex-1 flex-col justify-end">
        {/* Logo Display - Preview or Saved */}
        {displayUrl && (
          <div className="space-y-2">
            <div className="relative group">
              <div className="border rounded-lg p-4 bg-gray-50">
                <img 
                  src={displayUrl} 
                  alt={previewUrl ? `Preview ${type}` : `Current ${type}`}
                  className="max-w-full max-h-24 mx-auto object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
              <Button
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleDelete}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Change Logo Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={openFileDialog}
              disabled={uploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : `Change ${title}`}
            </Button>
          </div>
        )}

        {/* Upload Area - Only show if no logo is displayed */}
        {!displayUrl && (
          <div
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
              ${dragOver ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'}
              ${uploading ? 'opacity-50 pointer-events-none' : ''}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={openFileDialog}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600 mb-1">
              {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-gray-500">
              {acceptedFormats.join(', ')} up to {maxSize}MB
            </p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
          onChange={handleFileInputChange}
          className="hidden"
        />

        {!displayUrl && (
          <Button
            variant="outline"
            className="w-full"
            onClick={openFileDialog}
            disabled={uploading}
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Uploading...' : `Upload ${title}`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default LogoUpload;